import { memo, useCallback, useEffect, useRef, useState, type Ref } from "react";
import { useVideoPlayer as useExpoVideoPlayer, VideoView } from "expo-video";
import { VideoPlayer } from "@visioni/a3s-ui";
import { VideoControlButton } from "@src/features/clips/components/VideoControlButton";
import type { VideoViewRef } from "@src/features/clips/components/VideoPlayerView";
import type { Clip } from "@src/features/clips";

interface CompilationVideoPlayerProps {
  /** `null` until the watched compilation has a signed video URL. */
  readonly clip: Clip | null;
  /** Fill the parent instead of deriving a 16:9 box — used by the web layout. */
  readonly fillParent?: boolean | undefined;
  /** Ceiling (px) on the derived box height. Ignored when `fillParent` is set. */
  readonly maxHeight?: number | undefined;
  /**
   * Called once per compilation, when its first frame renders — i.e. playback
   * actually started, not just that the view opened. Mirrors `ClipVideoPlayer`.
   */
  readonly onViewed?: (() => void) | undefined;
}

/**
 * Compilation playback, on the shared `VideoPlayer` from `a3s-ui`.
 *
 * Deliberately its own component rather than the clips page's
 * `ClipVideoPlayer`. That one is still built on the bespoke overlay stack, and
 * migrating it is separate work touching every clips surface — see the
 * reasoning in `DEL-612`'s revert. This keeps the migration scoped to
 * compilations, which is the only screen that needs it here.
 *
 * Simpler than the clips player because a compilation is a single video: no
 * previous/next, no clip-index header, no per-clip analytics. Omitting the
 * `onPrevious`/`onNext` callbacks omits those controls entirely.
 */
function CompilationVideoPlayerInner({ clip, fillParent, maxHeight, onViewed }: CompilationVideoPlayerProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  // `VideoViewRef` rather than `VideoView`: expo-video merges its native and
  // web declarations, collapsing the instance type to `{}` — see the note on
  // that interface. The cast at the call site is the same one `VideoPlayerView`
  // makes for the same reason.
  const videoViewRef = useRef<VideoViewRef>(null);

  const player = useExpoVideoPlayer(clip?.videoUrl ?? null, (p) => {
    p.loop = false;
    // Muted so autoplay is allowed by browser policy; the viewer opts into
    // audio with the mute toggle.
    p.muted = true;
    p.play();
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // A different compilation means different media in the same player instance,
  // so the derived state has to be reset by hand — `mutedChange` in particular
  // won't fire when the previous video was muted too, leaving a stale icon.
  useEffect(() => {
    setVideoLoaded(false);
    setIsPlaying(false);
    setIsEnded(false);
    setIsMuted(true);
    setCurrentTime(0);
    setDuration(0);
  }, [clip?.id]);

  useEffect(() => {
    const playing = player.addListener("playingChange", ({ isPlaying: next }) => {
      setIsPlaying(next);
    });
    const ended = player.addListener("playToEnd", () => {
      setIsEnded(true);
    });
    const muted = player.addListener("mutedChange", ({ muted: next }) => {
      setIsMuted(next);
    });
    return () => {
      playing.remove();
      ended.remove();
      muted.remove();
    };
  }, [player]);

  // expo-video exposes position as a plain property with no change event, so
  // the scrub bar needs polling. 100ms is fine for a progress bar and stops
  // with the component.
  useEffect(() => {
    const update = () => {
      if (player.currentTime >= 0) setCurrentTime(player.currentTime);
      if (player.duration > 0) setDuration(player.duration);
    };
    update();
    const intervalId = setInterval(update, 100);
    return () => {
      clearInterval(intervalId);
    };
  }, [player]);

  // expo-video can fire `onFirstFrameRender` again for the same source (e.g.
  // after the surface remounts on a fullscreen toggle), so the id is held to
  // record each compilation once.
  const viewedClipIdRef = useRef<string | null>(null);
  const handleFirstFrameRender = useCallback(() => {
    setVideoLoaded(true);
    if (clip && viewedClipIdRef.current !== clip.id) {
      viewedClipIdRef.current = clip.id;
      onViewed?.();
    }
  }, [clip, onViewed]);

  const handlePlayPause = useCallback(() => {
    if (isEnded) {
      player.currentTime = 0;
      setIsEnded(false);
      player.play();
    } else if (isPlaying) {
      player.pause();
    } else {
      player.play();
    }
  }, [isEnded, isPlaying, player]);

  const handleSeek = useCallback(
    (time: number) => {
      player.currentTime = time;
      setCurrentTime(time);
      if (duration > 0 && time < duration) setIsEnded(false);
    },
    [player, duration],
  );

  const handleMuteToggle = useCallback(() => {
    player.muted = !player.muted;
  }, [player]);

  const handleFullscreenToggle = useCallback(() => {
    void (async () => {
      const view = videoViewRef.current;
      if (!view) return;
      try {
        if (isFullscreen) {
          await view.exitFullscreen();
        } else {
          await view.enterFullscreen();
        }
      } catch (error: unknown) {
        console.warn("Failed to toggle fullscreen:", error);
      }
    })();
  }, [isFullscreen]);

  if (!clip) return null;

  return (
    <VideoPlayer
      renderVideoSurface={({ style }) => (
        <VideoView
          ref={videoViewRef as unknown as Ref<never>}
          // @ts-expect-error expo-video merges native + web VideoView declarations, creating an unsatisfiable intersection type for `player`
          player={player}
          style={style}
          contentFit="contain"
          nativeControls={isFullscreen}
          allowsFullscreen
          allowsVideoFrameAnalysis={false}
          onFullscreenEnter={() => {
            setIsFullscreen(true);
          }}
          onFullscreenExit={() => {
            setIsFullscreen(false);
          }}
          onFirstFrameRender={handleFirstFrameRender}
        />
      )}
      playing={isPlaying}
      ended={isEnded}
      muted={isMuted}
      currentTime={currentTime}
      duration={duration}
      onSeek={handleSeek}
      onPlayPause={handlePlayPause}
      onMuteToggle={handleMuteToggle}
      onFullscreenToggle={handleFullscreenToggle}
      fullscreen={isFullscreen}
      // Chrome visibility lives in `VideoPlayer`, so controls revealed on one
      // compilation would otherwise still be showing on the next one.
      resetControlsKey={clip.id}
      poster={clip.thumbnailUrl ?? undefined}
      loading={!videoLoaded}
      fillParent={fillParent}
      maxHeight={maxHeight}
      renderPlayPauseButton={({ playing, ended, onPress }) => (
        <VideoControlButton
          icon={
            ended ? "refresh"
            : playing ?
              "pause"
            : "play"
          }
          onPress={onPress}
          size="large"
          accessibilityLabel={
            ended ? "Replay"
            : playing ?
              "Pause"
            : "Play"
          }
        />
      )}
      renderMuteButton={({ muted, onPress }) => (
        <VideoControlButton
          icon={muted ? "volumeOff" : "volumeOn"}
          onPress={onPress}
          accessibilityLabel={muted ? "Unmute audio" : "Mute audio"}
        />
      )}
      renderFullscreenButton={({ fullscreen, onPress }) => (
        <VideoControlButton
          icon={fullscreen ? "fullscreenExit" : "fullscreen"}
          onPress={onPress}
          accessibilityLabel={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        />
      )}
      renderExitFullscreenButton={({ onPress }) => (
        <VideoControlButton icon="close" onPress={onPress} accessibilityLabel="Exit fullscreen" />
      )}
    />
  );
}

CompilationVideoPlayerInner.displayName = "CompilationVideoPlayer";

export const CompilationVideoPlayer = memo(CompilationVideoPlayerInner);
